<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { ConversationInfo } from '../api/types'
import { displayName, formatTime, noticeText, replyTree, threadSummaries, timelineItems, type ThreadSummary } from '../lib/format'
import { annotations } from '../lib/annotations'
import { mayModerate } from '../lib/roles'
import { useSectionNav } from '../lib/sectionNav'
import MessageRow from './MessageRow.vue'
import { useSession } from '../stores/session'
import { useUnread } from '../stores/unread'
import Avatar from './Avatar.vue'
import CallCard from './CallCard.vue'
import PartyCard from './PartyCard.vue'

const props = defineProps<{ conv: ConversationInfo }>()
const session = useSession()
const unread = useUnread()
const scroller = ref<HTMLElement | null>(null)
const route = useRoute()
const router = useRouter()
const highlighted = ref<string | null>(null)
const threadNav = useSectionNav('thread')

const msgs = computed(() => session.messages[props.conv.id] ?? [])
// Replies live in their thread's panel; the channel shows a line under the message.
const tree = computed(() => unread.trees[props.conv.id] ?? replyTree(msgs.value))
const items = computed(() => timelineItems(msgs.value, tree.value.threaded))
const threads = computed(() => {
  const out = new Map<string, ThreadSummary & { people: string[] }>()
  for (const t of threadSummaries(msgs.value, tree.value)) {
    const people = [...new Set(msgs.value.filter((m) => tree.value.roots.get(m.envelope.id) === t.root.envelope.id).map((m) => m.sender.user_id))]
    out.set(t.root.envelope.id, { ...t, people: people.slice(0, 3) })
  }
  return out
})
const space = computed(() => session.spaces.find((s) => s.id === props.conv.space_id) ?? null)
// Hides count only from those who may hide that author's messages in this space.
const ann = computed(() =>
  annotations(msgs.value, (actor, author) => !!space.value && mayModerate(space.value, actor, author, 'hide_messages')),
)
const byId = computed(() => new Map(msgs.value.map((m) => [m.envelope.id, m])))
const joined = computed(() => session.engine?.hasGroup(props.conv.id) ?? false)

function nearBottom(): boolean {
  const el = scroller.value
  return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120
}

async function scrollToBottom(): Promise<void> {
  await nextTick()
  const el = scroller.value
  if (el) el.scrollTop = el.scrollHeight
}

watch(
  () => props.conv.id,
  () => void scrollToBottom(),
  { immediate: true },
)
// The call card appears at the end; keep it in view for whoever was reading there.
watch(
  () => !!session.inCall[props.conv.id]?.length,
  (on) => {
    if (on && nearBottom()) void scrollToBottom()
  },
)

// Jump to a message (e.g. from search): ?m=<message id>.
watch(
  () => [route.query.m, msgs.value.length] as const,
  async ([m]) => {
    if (typeof m !== 'string') return
    // A reply is in its thread: open that (the panel finds the reply) and show its message here.
    const root = tree.value.roots.get(m)
    if (root && threadNav.at.value !== root) void router.replace({ query: { ...route.query, thread: root } })
    await nextTick()
    const el = scroller.value?.querySelector(`[data-msg-id="${CSS.escape(root ?? m)}"]`)
    if (!el) return
    el.scrollIntoView({ block: 'center' })
    highlighted.value = root ?? m
    setTimeout(() => (highlighted.value = null), 2500)
  },
  { immediate: true, flush: 'post' },
)
watch(
  () => msgs.value.length,
  (n, old) => {
    const last = msgs.value[msgs.value.length - 1]
    // Replies land in their thread, not at the bottom here.
    if (!last || n <= (old ?? 0) || tree.value.threaded.has(last.envelope.id)) return
    // Always follow our own messages; follow others' only if already at the bottom.
    if (nearBottom() || last.sender.user_id === session.me) void scrollToBottom()
  },
  { flush: 'pre' },
)
</script>

<template>
  <div ref="scroller" class="scroll-thin flex-1 overflow-y-auto px-6 py-6 max-md:px-4" data-testid="timeline">
    <div class="mb-8 border-b border-border pb-6">
      <div class="mono mb-3 flex flex-wrap gap-x-4 gap-y-1 text-text-muted uppercase">
        <span>{{ conv.kind === 'channel' ? 'Channel' : 'Direct' }}</span>
        <span>MLS · E2EE</span>
        <span>{{ conv.members.length }} {{ conv.members.length === 1 ? 'member' : 'members' }}</span>
        <span>Epoch {{ conv.epoch }}</span>
        <span>{{ conv.id.slice(0, 8) }}</span>
      </div>
      <h2 class="text-3xl font-light tracking-tight">
        {{ conv.kind === 'channel' ? `Welcome to #${conv.name}` : 'Your private conversation' }}
      </h2>
      <p class="mt-2 max-w-xl text-sm text-text-muted">
        Messages here are end-to-end encrypted. Not even the server can read them. New members see messages from
        when they join.
      </p>
      <p v-if="!joined" class="mono mt-3 flex items-center gap-2 text-signal">
        <span class="h-1.5 w-1.5 animate-blink bg-signal" />Setting up encryption for this conversation…
      </p>
    </div>

    <template v-for="item in items" :key="item.kind === 'group' ? item.messages[0]!.seq : item.msg.seq">
      <PartyCard v-if="item.kind === 'party'" :msg="item.msg" />
      <div
        v-else-if="item.kind === 'notice' && item.msg.envelope.type === 'system'"
        class="mono mt-4 flex items-center gap-3 text-text-muted"
        data-testid="notice"
      >
        <span aria-hidden="true" class="text-text-disabled">{{ item.msg.envelope.event.startsWith('device') ? '[key]' : item.msg.envelope.event === 'left' ? '[−]' : '[+]' }}</span>
        <span>{{ noticeText(item.msg.envelope.event, displayName(session.profiles, item.msg.envelope.user)) }}</span>
        <time class="text-text-disabled">{{ formatTime(item.msg.receivedAt) }}</time>
      </div>
      <div
        v-else-if="item.kind === 'notice' && item.msg.envelope.type === 'conv_name'"
        class="mono mt-4 flex items-center gap-3 text-text-muted"
        data-testid="notice-renamed"
      >
        <span aria-hidden="true" class="text-text-disabled">[~]</span>
        <span>{{
          item.msg.envelope.name
            ? `${displayName(session.profiles, item.msg.sender.user_id)} named the group “${item.msg.envelope.name}”`
            : `${displayName(session.profiles, item.msg.sender.user_id)} removed the group's name`
        }}</span>
        <time class="text-text-disabled">{{ formatTime(item.msg.receivedAt) }}</time>
      </div>
      <div v-else-if="item.kind === 'group'" class="group mt-5 flex gap-3" data-testid="message-group">
        <Avatar :id="item.sender" :name="displayName(session.profiles, item.sender)" :size="32" />
        <div class="min-w-0 flex-1">
          <template v-for="(m, i) in item.messages" :key="m.seq">
            <MessageRow
              :class="highlighted === m.envelope.id ? 'bg-bg-hover ring-1 ring-ink' : ''"
              :msg="m"
              :conv-id="conv.id"
              :state="ann.get(m.envelope.id)"
              :reply-to="m.envelope.type === 'text' && m.envelope.reply_to ? byId.get(m.envelope.reply_to) : undefined"
              :reply-state="m.envelope.type === 'text' && m.envelope.reply_to ? ann.get(m.envelope.reply_to) : undefined"
            >
              <!-- Inside the first message, so hovering or pressing the name reaches its actions -->
              <template v-if="i === 0" #head>
                <div class="flex items-baseline gap-3">
                  <span class="text-sm font-medium" data-testid="message-author">{{ displayName(session.profiles, item.sender) }}</span>
                  <time class="mono text-text-disabled" :datetime="new Date(item.firstTs).toISOString()">
                    {{ formatTime(item.firstTs) }}
                  </time>
                </div>
              </template>
            </MessageRow>
            <!-- Its thread: who replied, how many, when; opens the thread's panel -->
            <div v-if="threads.get(m.envelope.id)" class="mt-1 mb-1 flex min-w-0 items-center gap-3 max-sm:gap-2">
              <button
                class="group/thread -mx-1 flex min-w-0 items-center gap-2 border border-transparent px-1 py-0.5 text-left hover:border-border hover:bg-bg-primary"
                :class="threadNav.at.value === m.envelope.id ? 'border-border bg-bg-primary' : ''"
                data-testid="thread-summary"
                @click="threadNav.at.value === m.envelope.id ? threadNav.close() : threadNav.open(m.envelope.id)"
              >
                <span class="flex shrink-0 -space-x-1">
                  <Avatar v-for="u in threads.get(m.envelope.id)!.people" :key="u" :id="u" :name="displayName(session.profiles, u)" :size="20" />
                </span>
                <span class="shrink-0 text-sm font-medium whitespace-nowrap text-text-primary underline-offset-2 group-hover/thread:underline">
                  {{ threads.get(m.envelope.id)!.replies }} {{ threads.get(m.envelope.id)!.replies === 1 ? 'reply' : 'replies' }}
                </span>
                <!-- Phones keep just the time, so the line never wraps -->
                <span class="mono min-w-0 truncate text-text-disabled">
                  <span class="max-sm:hidden">Last reply </span>{{ formatTime(threads.get(m.envelope.id)!.last.envelope.ts || threads.get(m.envelope.id)!.last.receivedAt) }}
                </span>
              </button>
              <button
                class="mono w-[5ch] shrink-0 text-left text-text-disabled uppercase hover:text-text-primary"
                :title="unread.mutedThreads.has(m.envelope.id) ? 'Unmute this thread' : 'Mute this thread: its replies don\'t notify or count as unread on this device'"
                data-testid="thread-mute-inline"
                @click="unread.muteThread(m.envelope.id, !unread.mutedThreads.has(m.envelope.id))"
              >
                {{ unread.mutedThreads.has(m.envelope.id) ? 'Muted' : 'Mute' }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </template>
    <CallCard :conv="conv.id" />
  </div>
</template>
